<template>
	<StandardModal
		v-if="type"
		open
		medium
		:title="modalTitle"
		hideConfirm
		:cancelText="$t('common_button_close')"
		@cancel="$emit('close')"
		@close="$emit('close')"
	>
		<div class="hidden-items-modal">
			<div class="hidden-items-search">
				<Input
					v-model="searchQuery"
					:placeholder="searchPlaceholder"
					:icon="SearchIcon"
					allowClear
				/>
			</div>

			<Table
				:columns="columns"
				:rows="items"
				keyField="id"
				hideHeader
				infinite
				:loadingOverlay="loading"
				:loadingMore="loadingMore"
				:emptyText="$t('settings_hidden_groups_empty_title')"
				:rowHeight="60"
				height="100%"
				@load-more="loadMore"
			>
				<template #cell-info="{ data }">
					<div class="hidden-item-content">
						<!-- DM row: profile pic + name -->
						<template v-if="type === 'dm'">
							<ProfilePizza
								v-if="isMultiDm(data)"
								:userIds="getDmMemberIds(data)"
								:size="36"
								showTotal
								alphabetical
								class="hidden-item-avatar"
							/>
							<ProfilePic
								v-else
								:user="getDmPartner(data)"
								size="36px"
								class="hidden-item-avatar"
							/>
							<div class="hidden-item-name">
								{{ data.chat.title }}
							</div>
						</template>

						<!-- Thread row: thread name + parent chat -->
						<template v-else-if="type === 'thread'">
							<div class="hidden-item-info">
								<div class="hidden-item-title">
									{{ data.chat.title }}
								</div>
								<div
									v-if="getParentChatTitle(data)"
									class="hidden-item-subtitle"
								>
									<UserGroupIcon type="solid" class="hidden-item-icon" />
									{{ getParentChatTitle(data) }}
								</div>
							</div>
						</template>

						<!-- Group row: category + group name -->
						<template v-else>
							<div class="hidden-item-info">
								<div v-if="data.chat.category" class="hidden-item-category">
									{{
										data.chat.category.user_category
											? data.chat.category.user_category.alias
											: data.chat.category.title
									}}
								</div>
								<div class="hidden-item-title">
									{{ data.chat.title }}
								</div>
							</div>
						</template>
					</div>
				</template>

				<template #cell-action="{ data }">
					<div class="hidden-item-action">
						<Button
							primary
							@click="unhideItem(data)"
							:loading="unhidingId === data.id"
						>
							{{ $t('chat_drawer_action_unhide') }}
						</Button>
					</div>
				</template>
			</Table>
		</div>
	</StandardModal>
</template>

<script>
import {
	setMembership,
	getMyMembershipForBubble,
	getMyUser,
	getMyUserId,
	updateUser as updateUserInStore,
} from '@/store'
import {
	StandardModal,
	Button,
	Input,
	ProfilePic,
	ProfilePizza,
	Table,
} from '@/components/common'
import { SearchIcon, UserGroupIcon } from '@/icons'
import { get } from '@/api/rest'
import { post } from '@/helpers/api'
import EventBus from '@/helpers/bus'
import debounce from 'debounce'

const FETCH_PARAMS = {
	group: {
		'filter[chat.is_dm]': 0,
		'filter[chat.is_thread]': 0,
		'filter[hidden]': 1,
		include: 'chat,chat.category.user_category',
		sort: 'chat.title',
	},
	thread: {
		'filter[chat.is_thread]': 1,
		'filter[hidden]': 1,
		include: 'chat',
		sort: 'chat.title',
	},
	dm: {
		'filter[chat.is_dm]': 1,
		'filter[hidden]': 1,
		include: 'chat,chat.memberships.user',
		sort: 'chat.title',
	},
}

export default {
	name: 'HiddenItemsModal',
	components: {
		StandardModal,
		Button,
		Input,
		ProfilePic,
		ProfilePizza,
		Table,
		UserGroupIcon,
	},
	props: {
		type: {
			type: String,
			required: true,
			validator: (v) => ['group', 'thread', 'dm'].includes(v),
		},
	},
	emits: ['close', 'unhide'],
	data() {
		return {
			loading: true,
			loadingMore: false,
			items: [],
			apiResponse: null,
			searchQuery: '',
			unhidingId: null,
			SearchIcon,
		}
	},
	created() {
		this.fetchItemsDebounced = debounce(this.fetchItems.bind(this), 500)
	},
	mounted() {
		this.fetchItems()
	},
	beforeUnmount() {
		this.fetchItemsDebounced.clear()
	},
	watch: {
		searchQuery() {
			this.loading = true
			this.fetchItemsDebounced()
		},
	},
	computed: {
		modalTitle() {
			const titles = {
				group: this.$t('settings_hidden_groups_view_hidden_groups'),
				thread: this.$t('settings_hidden_groups_view_hidden_threads'),
				dm: this.$t('settings_hidden_groups_view_hidden_dms'),
			}
			return titles[this.type] || ''
		},
		searchPlaceholder() {
			const placeholders = {
				group: this.$t('common_search'),
				thread: this.$t('common_search'),
				dm: this.$t('common_search'),
			}
			return placeholders[this.type] || ''
		},
		columns() {
			return [
				{ field: 'info', width: '100%' },
				{ field: 'action', minWidth: '135px', align: 'right' },
			]
		},
	},
	methods: {
		async fetchItems() {
			this.loading = true
			this.items = []
			try {
				const id = getMyUserId()
				const params = {
					'page[size]': 100,
					...FETCH_PARAMS[this.type],
				}
				if (this.searchQuery) {
					params['filter[search]'] = this.searchQuery
				}
				this.apiResponse = await get(`/users/${id}/memberships`, params)
				this.items = this.apiResponse.data
			} catch (error) {
				console.error(error)
			}
			this.loading = false
		},
		async loadMore() {
			if (!this.apiResponse?.next) return
			this.loadingMore = true
			try {
				this.apiResponse = await this.apiResponse.next()
				this.items = this.items.concat(this.apiResponse.data)
			} catch (error) {
				console.error(error)
			}
			this.loadingMore = false
		},
		isMultiDm(item) {
			return item.chat?.dm_memberships_count > 2
		},
		getDmMemberIds(item) {
			const memberships = item.chat?.memberships || []
			return memberships.map((m) => m.user_id)
		},
		getDmPartner(item) {
			const myId = getMyUserId()
			const memberships = item.chat?.memberships || []
			const partner = memberships.find((m) => m.user_id !== myId)
			if (!partner?.user) return {}
			const user = partner.user
			return {
				...user,
				profilepic: user.hasOwnProperty('profilepic')
					? user.profilepic
					: !!user.profilepicpath,
			}
		},
		getParentChatTitle(item) {
			return item.chat?.parent_chat?.title || null
		},
		async unhideItem(item) {
			this.unhidingId = item.id
			const me = getMyUser()
			const prevCount =
				this.type === 'group' ? me?.hidden_group_memberships_count ?? 0 : null

			const membership = getMyMembershipForBubble(item.chat_id)
			if (membership) {
				membership.ishidden = false
				setMembership(membership)
			}
			if (prevCount !== null) {
				updateUserInStore(getMyUserId(), {
					hidden_group_memberships_count: Math.max(0, prevCount - 1),
				})
			}

			try {
				const { data } = await post('/api/v1/membership.update', {
					bubble_id: item.chat_id,
					ishidden: 0,
				})

				if (!data.ok) {
					throw new Error(data.error || 'Failed to unhide')
				}

				this.items = this.items.filter((i) => i.id !== item.id)
				EventBus.$emit('groupHidden')
				this.$emit('unhide', this.type)
			} catch (error) {
				console.error(error)
				this.$alert?.error(error)
				if (membership) {
					membership.ishidden = true
					setMembership(membership)
				}
				if (prevCount !== null) {
					updateUserInStore(getMyUserId(), {
						hidden_group_memberships_count: prevCount,
					})
				}
			}
			this.unhidingId = null
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.hidden-items-modal {
	display: flex;
	flex-direction: column;
	height: 100%;
}

.hidden-items-search {
	padding: 16px 24px;
	flex-shrink: 0;
	border-bottom: 1px solid var(--neutral-200);
}

.hidden-item-content {
	display: flex;
	align-items: center;
	width: 100%;
}

.hidden-item-avatar {
	flex-shrink: 0;
	margin-right: 12px;
}

.hidden-item-name {
	font-family: Avenir-Roman;
	font-size: 16px;
	color: var(--neutral-800);
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.hidden-item-info {
	flex: 1;
	min-width: 0;
}

.hidden-item-category {
	font-family: Avenir-Roman;
	font-size: 12px;
	text-transform: uppercase;
	color: var(--neutral-600);
	margin-bottom: 4px;
}

.hidden-item-title {
	font-family: Avenir-Heavy;
	font-size: 15px;
	color: var(--neutral-800);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.hidden-item-subtitle {
	display: flex;
	align-items: center;
	gap: 4px;
	font-family: Avenir-Book;
	font-size: 12px;
	text-transform: uppercase;
	color: var(--neutral-600);
	letter-spacing: 0.5px;
	margin-top: 4px;
}

.hidden-item-icon {
	font-size: 11px;
	color: var(--neutral-600);
}

.hidden-item-action {
	flex-shrink: 0;
	opacity: 0;
	transition: opacity 150ms ease;

	&:focus-within {
		opacity: 1;
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

.hidden-items-modal {
	.pronto-table {
		flex: 1;
		min-height: 0;

		.table-body {
			height: 100% !important;
			background: transparent;
		}

		.table-row {
			.table-cell:first-child {
				padding-left: 24px;
			}

			.table-cell:last-child {
				padding-right: 24px;
			}

			&:hover:not(.table-header),
			&:focus-within:not(.table-header) {
				.hidden-item-action {
					opacity: 1;
				}
			}
		}
	}
}

@include overrideScheme('dark') {
	.hidden-items-modal {
		.pronto-table .table-body {
			background: transparent;
		}
	}
}
</style>
